export const BOOKING_PORTAL_BLOCK_TYPE = "bookingPortal";
export const BOOKING_SYSTEM_PAGE_KEY = "system:booking";

export const BOOKING_PORTAL_VARIANTS = [
    "guided",
    "schedule",
    "professional",
    "venue-grid",
    "quick-slots",
] as const;

export type BookingPortalVariant = typeof BOOKING_PORTAL_VARIANTS[number];

export type BookingPortalFlowStep = "service" | "resource" | "schedule" | "contact";

export type BookingPortalExperience =
    | "guided-wizard"
    | "availability-board"
    | "professional-first"
    | "venue-grid"
    | "quick-slots";

export type BookingPortalCheckoutPresentation = "page" | "dialog";

export type BookingPortalVariantContract = {
    experience: BookingPortalExperience;
    steps: readonly BookingPortalFlowStep[];
    checkout: BookingPortalCheckoutPresentation;
    initialSelection: "none" | "first-service";
};

export const BOOKING_PORTAL_VARIANT_CONTRACTS: Readonly<
    Record<BookingPortalVariant, BookingPortalVariantContract>
> = {
    guided: {
        experience: "guided-wizard",
        steps: ["service", "resource", "schedule", "contact"],
        checkout: "page",
        initialSelection: "none",
    },
    schedule: {
        experience: "availability-board",
        steps: ["service", "schedule", "contact"],
        checkout: "page",
        initialSelection: "first-service",
    },
    professional: {
        experience: "professional-first",
        steps: ["resource", "service", "schedule", "contact"],
        checkout: "page",
        initialSelection: "none",
    },
    "venue-grid": {
        experience: "venue-grid",
        steps: ["service", "schedule", "contact"],
        checkout: "dialog",
        initialSelection: "first-service",
    },
    "quick-slots": {
        experience: "quick-slots",
        steps: ["service", "schedule", "contact"],
        checkout: "dialog",
        initialSelection: "first-service",
    },
};

export const BOOKING_PORTAL_VARIANT_BLUEPRINTS: Readonly<
    Record<BookingPortalVariant, readonly BookingPortalFlowStep[]>
> = Object.fromEntries(
    Object.entries(BOOKING_PORTAL_VARIANT_CONTRACTS).map(([variant, contract]) => [
        variant,
        contract.steps,
    ]),
) as Record<BookingPortalVariant, readonly BookingPortalFlowStep[]>;

export type PublicBookingPortal = {
    uuid: string;
    name: string;
    workspaceUuid: string;
    timezone: string;
    siteUuid?: string;
    apiBasePath?: string;
};

export type PublicBookingSubmission = {
    portal: PublicBookingPortal;
    serviceUuid: string;
    resourceUuid: string | null;
    customerName: string;
    customerEmail: string;
    customerPhone: string | null;
    startAt: string;
    capacityUnits: number;
    notes: string | null;
};

export type PublicBookingPayload = Omit<PublicBookingSubmission, "portal"> & {
    siteUuid: string | null;
};

export function bookingPortalSlotsSearchParams(input: {
    serviceUuid: string;
    resourceUuid?: string | null;
    capacityUnits: number;
    from: Date;
    to: Date;
}): URLSearchParams {
    const query = new URLSearchParams({
        serviceUuid: input.serviceUuid,
        capacityUnits: String(input.capacityUnits),
        from: input.from.toISOString(),
        to: input.to.toISOString(),
    });
    if (input.resourceUuid) query.set("resourceUuid", input.resourceUuid);

    return query;
}

export function bookingPortalPublicBookingPayload(
    input: PublicBookingSubmission,
): PublicBookingPayload {
    const { portal, ...booking } = input;

    return {
        ...booking,
        siteUuid: portal.siteUuid ?? null,
    };
}

export type BookingPortalAvailability = {
    bookingPortal?: PublicBookingPortal | null;
    hasBookingPortal?: boolean;
};

export type BookingPortalSlotProjection<T> = {
    slot: T;
    startAt: Date;
    startAtMilliseconds: number;
    dayKey: string;
    hour: number;
    minute: number;
};

export type BookingPortalSlotIndex<T> = {
    slots: readonly BookingPortalSlotProjection<T>[];
    byDay: ReadonlyMap<string, readonly BookingPortalSlotProjection<T>[]>;
    byDayHour: ReadonlyMap<string, readonly BookingPortalSlotProjection<T>[]>;
};

export function siteHasBookingPortal(
    site: BookingPortalAvailability | null | undefined,
): boolean {
    return site?.hasBookingPortal ?? site?.bookingPortal != null;
}

type BookingPortalBlockNode = {
    type?: string;
    children?: BookingPortalBlockNode[];
    slots?: Record<string, BookingPortalBlockNode[]>;
};

export function hasBookingPortalBlock(document: unknown): boolean {
    if (!document || typeof document !== "object") return false;
    const blocks = (document as { blocks?: unknown }).blocks;
    if (!Array.isArray(blocks)) return false;

    const containsPortal = (items: BookingPortalBlockNode[]): boolean => items.some((block) => (
        block.type === BOOKING_PORTAL_BLOCK_TYPE
        || containsPortal(Array.isArray(block.children) ? block.children : [])
        || Object.values(block.slots ?? {}).some((slot) => (
            containsPortal(Array.isArray(slot) ? slot : [])
        ))
    ));

    return containsPortal(blocks as BookingPortalBlockNode[]);
}

export function normalizeBookingPortalVariant(value: unknown): BookingPortalVariant {
    return typeof value === "string"
        && (BOOKING_PORTAL_VARIANTS as readonly string[]).includes(value)
        ? value as BookingPortalVariant
        : "guided";
}

export const DEFAULT_BOOKING_PORTAL_TIMEZONE = "Europe/Prague";

type BookingPortalDateParts = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
};

const DATE_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;
const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();
const displayDateFormatters = new Map<string, Intl.DateTimeFormat>();
const normalizedTimezones = new Map<string, string>();

export function normalizeBookingPortalTimezone(value: unknown): string {
    if (typeof value !== "string" || value.trim() === "") {
        return DEFAULT_BOOKING_PORTAL_TIMEZONE;
    }

    const cached = normalizedTimezones.get(value);
    if (cached) return cached;

    try {
        new Intl.DateTimeFormat("en", { timeZone: value }).format(0);
        normalizedTimezones.set(value, value);
        return value;
    } catch {
        normalizedTimezones.set(value, DEFAULT_BOOKING_PORTAL_TIMEZONE);
        return DEFAULT_BOOKING_PORTAL_TIMEZONE;
    }
}

function bookingPortalDateTimeParts(date: Date, timezone: string): BookingPortalDateParts {
    const safeTimezone = normalizeBookingPortalTimezone(timezone);
    let formatter = dateTimeFormatters.get(safeTimezone);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat("en-CA", {
            day: "2-digit",
            hour: "2-digit",
            hourCycle: "h23",
            minute: "2-digit",
            month: "2-digit",
            second: "2-digit",
            timeZone: safeTimezone,
            year: "numeric",
        });
        dateTimeFormatters.set(safeTimezone, formatter);
    }

    const values = Object.fromEntries(
        formatter.formatToParts(date)
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)]),
    ) as Record<string, number>;

    return {
        year: values.year ?? 0,
        month: values.month ?? 1,
        day: values.day ?? 1,
        hour: values.hour ?? 0,
        minute: values.minute ?? 0,
        second: values.second ?? 0,
    };
}

function bookingPortalParseDayKey(dayKey: string): Pick<BookingPortalDateParts, "year" | "month" | "day"> {
    const match = DATE_KEY_PATTERN.exec(dayKey);
    if (!match) throw new RangeError(`Invalid booking day: ${dayKey}`);

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const validation = new Date(Date.UTC(year, month - 1, day));
    if (
        validation.getUTCFullYear() !== year
        || validation.getUTCMonth() !== month - 1
        || validation.getUTCDate() !== day
    ) {
        throw new RangeError(`Invalid booking day: ${dayKey}`);
    }

    return { year, month, day };
}

function bookingPortalCivilDayKey(dayKey: string, days: number): string {
    const { year, month, day } = bookingPortalParseDayKey(dayKey);
    const next = new Date(Date.UTC(year, month - 1, day + days));

    return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
}

export function bookingPortalDayKey(
    date: Date,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): string {
    const parts = bookingPortalDateTimeParts(date, timezone);

    return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

export function bookingPortalTimeParts(
    date: Date,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Pick<BookingPortalDateParts, "hour" | "minute" | "second"> {
    const { hour, minute, second } = bookingPortalDateTimeParts(date, timezone);
    return { hour, minute, second };
}

export function bookingPortalDateFormatter(
    locale: string,
    timezone: string,
    options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
    const safeTimezone = normalizeBookingPortalTimezone(timezone);
    const key = `${locale}:${safeTimezone}:${JSON.stringify(options)}`;
    let formatter = displayDateFormatters.get(key);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, { ...options, timeZone: safeTimezone });
        displayDateFormatters.set(key, formatter);
    }

    return formatter;
}

export function bookingPortalDayHourKey(dayKey: string, hour: number): string {
    return `${dayKey}:${hour}`;
}

/**
 * Parses and converts every availability instant exactly once. Consumers can
 * then render calendar cells with direct day/hour lookups instead of scanning
 * the entire availability response for every cell.
 */
export function indexBookingPortalSlots<T extends { startAt: string }>(
    slots: readonly T[],
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): BookingPortalSlotIndex<T> {
    const safeTimezone = normalizeBookingPortalTimezone(timezone);
    const byDay = new Map<string, BookingPortalSlotProjection<T>[]>();
    const byDayHour = new Map<string, BookingPortalSlotProjection<T>[]>();
    const projections = slots.map((slot) => {
        const startAt = new Date(slot.startAt);
        const parts = bookingPortalDateTimeParts(startAt, safeTimezone);
        const dayKey = `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
        const projection: BookingPortalSlotProjection<T> = {
            slot,
            startAt,
            startAtMilliseconds: startAt.getTime(),
            dayKey,
            hour: parts.hour,
            minute: parts.minute,
        };
        const daySlots = byDay.get(dayKey);
        if (daySlots) daySlots.push(projection);
        else byDay.set(dayKey, [projection]);

        const dayHourKey = bookingPortalDayHourKey(dayKey, parts.hour);
        const hourSlots = byDayHour.get(dayHourKey);
        if (hourSlots) hourSlots.push(projection);
        else byDayHour.set(dayHourKey, [projection]);

        return projection;
    });

    return { slots: projections, byDay, byDayHour };
}

/** Builds a new lookup index from existing projections without reparsing ISO instants. */
export function indexBookingPortalSlotProjections<T>(
    slots: readonly BookingPortalSlotProjection<T>[],
): BookingPortalSlotIndex<T> {
    const byDay = new Map<string, BookingPortalSlotProjection<T>[]>();
    const byDayHour = new Map<string, BookingPortalSlotProjection<T>[]>();
    slots.forEach((projection) => {
        const daySlots = byDay.get(projection.dayKey);
        if (daySlots) daySlots.push(projection);
        else byDay.set(projection.dayKey, [projection]);

        const dayHourKey = bookingPortalDayHourKey(projection.dayKey, projection.hour);
        const hourSlots = byDayHour.get(dayHourKey);
        if (hourSlots) hourSlots.push(projection);
        else byDayHour.set(dayHourKey, [projection]);
    });

    return { slots, byDay, byDayHour };
}

/** Converts an IANA-zone civil date/time into an explicit UTC instant. */
export function bookingPortalZonedDateTime(
    dayKey: string,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
    hour = 0,
    minute = 0,
): Date {
    const safeTimezone = normalizeBookingPortalTimezone(timezone);
    const { year, month, day } = bookingPortalParseDayKey(dayKey);
    const targetWallClock = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    let instant = targetWallClock;

    // The offset can change between the initial UTC guess and the requested wall
    // clock (DST). Re-evaluating converges for every valid civil boundary.
    for (let iteration = 0; iteration < 6; iteration += 1) {
        const observed = bookingPortalDateTimeParts(new Date(instant), safeTimezone);
        const observedWallClock = Date.UTC(
            observed.year,
            observed.month - 1,
            observed.day,
            observed.hour,
            observed.minute,
            observed.second,
            0,
        );
        const correction = targetWallClock - observedWallClock;
        if (correction === 0) return new Date(instant);
        instant += correction;
    }

    return new Date(instant);
}

export function bookingPortalDateFromDayKey(
    dayKey: string,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date {
    return bookingPortalZonedDateTime(dayKey, timezone);
}

export function bookingPortalStartOfDay(
    date: Date,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date {
    return bookingPortalDateFromDayKey(bookingPortalDayKey(date, timezone), timezone);
}

export function bookingPortalStartOfMonth(
    date: Date,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date {
    const dayKey = bookingPortalDayKey(date, timezone);
    return bookingPortalDateFromDayKey(`${dayKey.slice(0, 7)}-01`, timezone);
}

export function bookingPortalStartOfWeek(
    date: Date,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date {
    const dayKey = bookingPortalDayKey(date, timezone);
    const { year, month, day } = bookingPortalParseDayKey(dayKey);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    const daysSinceMonday = (weekday + 6) % 7;

    return bookingPortalDateFromDayKey(bookingPortalCivilDayKey(dayKey, -daysSinceMonday), timezone);
}

export function bookingPortalWeekDays(
    date: Date,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date[] {
    const start = bookingPortalStartOfWeek(date, timezone);

    return Array.from({ length: 7 }, (_, index) => (
        bookingPortalAddDays(start, index, timezone)
    ));
}

export function bookingPortalAddDays(
    date: Date,
    days: number,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date {
    return bookingPortalDateFromDayKey(
        bookingPortalCivilDayKey(bookingPortalDayKey(date, timezone), days),
        timezone,
    );
}

export function bookingPortalSlotKey(slot: {
    startAt: string;
    resourceUuid: string | null;
}): string {
    return `${slot.startAt}:${slot.resourceUuid ?? "any"}`;
}

export function mergeBookingPortalSlots<T extends {
    startAt: string;
    resourceUuid: string | null;
}>(collections: readonly (readonly T[])[]): T[] {
    const merged = new Map<string, T>();
    collections.forEach((collection) => collection.forEach((slot) => {
        merged.set(bookingPortalSlotKey(slot), slot);
    }));

    return [...merged.values()].sort((first, second) => (
        first.startAt.localeCompare(second.startAt)
        || (first.resourceUuid ?? "").localeCompare(second.resourceUuid ?? "")
    ));
}

export function bookingPortalAddMonths(
    date: Date,
    months: number,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date {
    const { year, month } = bookingPortalDateTimeParts(date, timezone);
    const target = new Date(Date.UTC(year, month - 1 + months, 1));
    const dayKey = `${target.getUTCFullYear()}-${String(target.getUTCMonth() + 1).padStart(2, "0")}-01`;

    return bookingPortalDateFromDayKey(dayKey, timezone);
}

export function bookingPortalCalendarDays(
    month: Date,
    timezone = DEFAULT_BOOKING_PORTAL_TIMEZONE,
): Date[] {
    const firstDay = bookingPortalStartOfMonth(month, timezone);
    const calendarStart = bookingPortalStartOfWeek(firstDay, timezone);

    return Array.from({ length: 42 }, (_, index) => (
        bookingPortalAddDays(calendarStart, index, timezone)
    ));
}

export function bookingPortalApiBasePath(portal: PublicBookingPortal): string {
    return portal.apiBasePath ?? (
        `/api/public/workspaces/${encodeURIComponent(portal.workspaceUuid)}`
        + `/booking/portals/${encodeURIComponent(portal.uuid)}`
    );
}
